<template>
    <div ref="wrapperEl" class="flex flex-col gap-6">
        <vx-box class="overflow-clip">
            <div
                class="inset-bs-0 z-10 flex flex-col gap-3 border-solid border-t-0 border-x-0 border-b border-b-slate-200 bg-white p-6 [@media(min-height:700px)]:sticky"
            >
                <div class="flex sm:items-center justify-between gap-2">
                    <div class="text-xl font-extrabold leading-none">
                        {{ activeTab === 'all' ? 'Select Locked Snippets' : 'Manage Selection' }}
                    </div>
                    <vx-button color="muted" @click="cancel()">
                        <font-awesome-icon widthAuto :icon="faXmark"></font-awesome-icon>
                    </vx-button>
                </div>
                <vx-tabs v-model="activeTab" class="bg-slate-100!">
                    <vx-tab value="all">All</vx-tab>
                    <vx-tab value="selected">Selected ({{ selectedSnippets.length }})</vx-tab>
                </vx-tabs>
                <vx-input
                    v-if="activeTab === 'all'"
                    ref="searchEl"
                    placeholder="Search"
                    v-model="search"
                    @keydown.enter="handleEnter"
                    @update:modelValue="searchFn(props.fetchParams, $event)"
                >
                    <template v-slot:right>
                        <font-awesome-icon widthAuto :icon="faMagnifyingGlass" class="text-sm"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
            <div v-if="activeTab === 'all'" class="flex flex-col gap-2 bg-slate-50 p-6">
                <template v-if="options && options.length > 0">
                    <analytics-select-snippet-item
                        v-for="snippet in options?.slice(0, 10)"
                        :key="`snippet_item_${snippet.snippet_id}_all`"
                        :snippet="snippet"
                        :search="search"
                        :selected="isSelected(snippet)"
                        @selected="toggleSnippet(snippet)"
                    />
                </template>

                <div v-else class="flex p-8 items-center justify-center flex-col gap-3">
                    <template v-if="loadingOptions">
                        <vx-icon-loading spin class="w-6 h-6"></vx-icon-loading>
                    </template>
                    <template v-else>
                        <font-awesome-icon widthAuto :icon="faSquareDashed" class="text-2xl"></font-awesome-icon>
                        <span class="font-normal text-lg">No results found</span>
                    </template>
                </div>
            </div>
            <div v-if="activeTab === 'selected'" class="flex flex-col gap-2 bg-slate-50 p-6">
                <template v-if="selectedSnippets.length > 0">
                    <analytics-select-snippet-item
                        v-for="snippet in selectedSnippets"
                        :key="`snippet_item_${snippet.snippet_id}_selected`"
                        :snippet="snippet"
                        :selected="true"
                        @selected="toggleSnippet(snippet)"
                    />
                </template>

                <div v-else class="flex p-8 items-center justify-center flex-col gap-3">
                    <font-awesome-icon widthAuto :icon="faSquareDashed" class="text-2xl"></font-awesome-icon>
                    <span class="font-normal text-lg">No selected snippets</span>
                </div>
            </div>
        </vx-box>

        <vx-sticky-footer class="w-full max-w-lg mx-auto">
            <vx-button class="grow" @click="apply()">Done with selection</vx-button>
        </vx-sticky-footer>
    </div>
</template>

<script setup lang="ts">
import AnalyticsSelectSnippetItem from './AnalyticsSelectSnippetItem.vue';
import { faMagnifyingGlass, faSquareDashed, faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxBox, VxInput, VxButton, VxTabs, VxTab, VxStickyFooter, VxIconLoading } from '@voxie/frontend-components';
import { nextTick, onMounted, ref, watch } from 'vue';
import { useAnalyticsSelectSnippets, type SelectedSnippet } from './useAnalyticsSelectSnippets';
import { useToasted } from '~/composables/useToasted';
import type { OutboundMessagesParams } from '~/services/analytics/outboundMessages/outboundMessagesAnalyticsClient.types';

const toasted = useToasted();
const { selectingSnippets, selectedSnippets, loadingOptions, options, searchFn, isSameSnippet, getSnippetById } =
    useAnalyticsSelectSnippets();

const props = defineProps<{
    orgUnitId?: number;
    groupId?: string;
    fetchParams: OutboundMessagesParams;
    modelValue?: number[];
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', value?: number[]): void;
}>();

onMounted(async () => {
    await nextTick();
    searchFn(props.fetchParams, search.value);
});

watch(
    () => props.fetchParams,
    async () => {
        searchFn(props.fetchParams, search.value);
    },
    {
        deep: true,
    }
);

watch(
    () => props.modelValue,
    (newValue) => {
        selectedSnippets.value =
            newValue && newValue.length > 0
                ? newValue.map((id) => {
                      const snippet = getSnippetById(id);
                      return snippet || { snippet_id: id };
                  })
                : [];
    },
    {
        deep: true,
        immediate: true,
    }
);

const cancel = () => {
    selectedSnippets.value =
        props.modelValue && props.modelValue.length > 0
            ? props.modelValue.map((id) => {
                  const snippet = getSnippetById(id);
                  return snippet || { snippet_id: id };
              })
            : [];
    selectingSnippets.value = false;
};

const apply = () => {
    emit(
        'update:modelValue',
        selectedSnippets.value.length ? selectedSnippets.value.map(({ snippet_id }) => snippet_id) : undefined
    );
    selectingSnippets.value = false;
};

const activeTab = ref('all');

const isSelected = (snippet: SelectedSnippet): boolean => {
    return selectedSnippets.value.some((currentSnippet) => isSameSnippet(currentSnippet, snippet));
};

const wrapperEl = ref<HTMLDivElement>();
watch(selectingSnippets, async () => {
    await nextTick();
    if (selectingSnippets.value && wrapperEl.value) {
        const rect = wrapperEl.value.getBoundingClientRect();
        window.scrollTo({
            top: window.scrollY + rect.top - 80,
            behavior: 'smooth',
        });
        searchEl.value?.field?.focus?.();
    } else {
        search.value = '';
    }
});

const handleEnter = () => {
    if (!search.value.length) {
        return;
    }

    if (loadingOptions.value) {
        return;
    }

    if (options.value && options.value[0]) {
        toggleSnippet(options.value[0]);
    }
};

const toggleSnippet = (snippet: SelectedSnippet): void => {
    if (isSelected(snippet)) {
        selectedSnippets.value = selectedSnippets.value.filter(
            (currentSnippet) => !isSameSnippet(currentSnippet, snippet)
        );
        return;
    }

    if (selectedSnippets.value.length >= 10) {
        toasted.error('You can only select up to 10 snippets.');
        return;
    }
    selectedSnippets.value.push(snippet);
};

const searchEl = ref<InstanceType<typeof VxInput>>();
const search = ref('');

defineExpose({
    apply,
});
</script>
